import { useMsal } from '@azure/msal-react';
import { Button, Checkbox, Link, makeStyles, MessageBar, MessageBarBody, Text } from '@fluentui/react-components';
import { AddCircleRegular, ArrowDownload16Regular, ChevronDownRegular, ChevronRightRegular } from '@fluentui/react-icons';
import parse from 'html-react-parser';
import { observer } from 'mobx-react-lite';
import React, { JSX, useState } from 'react';
import { calculateBytesFromMB } from '../../../common/calculation/calculate-bytes-in-mb';
import { downloadFile } from '../../../common/file/download-file';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { getCleanHtmlString } from '../../../common/html-string/get-clean-html-string';
import { DocumentType } from '../../../common/types/document-types';
import { executiveSummaryDocumentConfiguration, supportingDocumentConfiguration } from '../../../configurations';
import { Document, DocumentMetadata, OrganizationType, PartnerContact } from '../../../models';
import { ResourceTicketRequest, ResourceTypes } from '../../../models/resource-services';
import { isPdfFileName, isValidImageFile } from '../../../shared/functions';
import { UploadButton } from '../../shared-components';
import DocumentFile from '../../shared-components/DocumentFile';
import { FlexBox } from '../../shared-components/flex-box';
import { FormattedInput, FormInput } from '../../shared-components/form';
import { PartnerTypeSelectionDropdown } from '../../shared-components/partner-type-selection-dropdown/PartnerTypeSelectionDropdown';
import { RichTextEditor } from '../../shared-components/RichTextEditor';
import { ValidationMessageList } from '../../shared-components/ValidationMessageList';
import { CustomerReferences } from './CustomerReferences';
import JudgingQuestion from './JudgingQuestion';
import './NominationForStaffSummary.scss';
import { SummaryLayout } from './SummaryLayout';

const useStyles = makeStyles({
  expandableTriggerButton: { padding: 0 },
});

interface NominationForStaffSummaryProps {
  isJudgingValidated: boolean;
  downloadExportedNomination(nominationId: number): void;
  downloadUploadedMedia(): void;
  onCaseStudyCheckedChange(isCaseStudy: boolean): void;
  onCancelEditNomination(): void;
  onSaveNomination(): void;
  onRestoreDocument(documentType: DocumentType): void;
}

const NominationForStaffSummaryImpl: React.FC<NominationForStaffSummaryProps> = (props) => {
  const {
    isJudgingValidated,
    downloadUploadedMedia,
    downloadExportedNomination,
    onCaseStudyCheckedChange,
    onCancelEditNomination,
    onSaveNomination,
    onRestoreDocument,
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    awardStore,
    partnerTypeStore,
    userStore,
    nominationForStaffPageUiStore,
  } = useStores();

  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const companyInformationValidation = nominationForStaffPageUiStore.getCompanyInformationValidation(partnerTypeStore.otherPartnerTypeId);

  const partnerTypeDisplayNames = nominationForStaffPageUiStore.nominationForStaff.nomination.partnerTypes
    .map((type) => (partnerTypeStore.isOtherPartnerType(type.partnerTypeId) ? type.description : partnerTypeStore.getPartnerTypeDisplayName(type.partnerTypeId)))
    .join(', ');

  const [isAwardRequirementExpanded, setAwardRequirementExpanded] = useState<boolean>(false);
  const [executiveSummaryDocumentError, setExecutiveSummaryDocumentError] = useState<string>('');
  const [selectedSupportingDocumentError, setSelectedSupportingDocumentError] = useState<string>('');

  const handleEditNomination = (): void => {
    nominationForStaffPageUiStore.setIsBeingValidated(false);
    nominationForStaffPageUiStore.setIsEditingMode(true);
  };

  const handleAddDocumentToUpload = (documentType: DocumentType, content: File): void => {
    const sortedDocuments = nominationForStaffPageUiStore.documentToUpload.slice().sort((doc1, doc2) => doc1.documentId - doc2.documentId);
    const minId = (sortedDocuments[0]?.documentId ?? 0) - 1;
    const newDocument: Document = {
      nominationId: nominationForStaffPageUiStore.nominationForStaff.nominationId,
      documentId: minId,
      documentType,
      content,
    };

    const newDocumentMetadata: DocumentMetadata = {
      documentId: newDocument.documentId,
      nominationId: newDocument.nominationId,
      fileName: newDocument.content.name,
    };

    nominationForStaffPageUiStore.documentToUpload.push(newDocument);
    if (documentType === 'executive-summary') {
      nominationForStaffPageUiStore.setExecutiveSummaryDocument(newDocumentMetadata);
    } else {
      nominationForStaffPageUiStore.nominationForStaff.nomination.supportingDocuments.push(newDocumentMetadata);
    }
  };

  const handleUploadFile = async (documentType: DocumentType, files: FileList): Promise<void> => {
    if (!files || !files[0]) {
      return;
    }

    const file = files[0];
    const isValidImage = await isValidImageFile(file);

    if (documentType === 'executive-summary') {
      if (!executiveSummaryDocumentConfiguration.supportedFileTypes.includes(file.type)) {
        setExecutiveSummaryDocumentError('Select a supported file type.');
        return;
      } else if (file.size > calculateBytesFromMB(executiveSummaryDocumentConfiguration.maxFileSizeInMB)) {
        setExecutiveSummaryDocumentError(`Upload an executive summary document that is ${executiveSummaryDocumentConfiguration.maxFileSizeInMB} MB or smaller.`);
        return;
      } else if (!isValidImage) {
        setExecutiveSummaryDocumentError('Select a valid image file.');
        return;
      } else {
        setExecutiveSummaryDocumentError('');
      }
    } else if (documentType === 'supporting-document') {
      const maxSupportingDocumentCount = 1;
      if (nominationForStaffPageUiStore.nominationForStaff.nomination.supportingDocuments.length >= maxSupportingDocumentCount) {
        setSelectedSupportingDocumentError('Upload only one supporting document for each nomination.');
        return;
      } else if (!supportingDocumentConfiguration.supportedFileTypes.includes(file.type)) {
        setSelectedSupportingDocumentError('Select a supported file type.');
        return;
      } else if (file.size > calculateBytesFromMB(supportingDocumentConfiguration.maxFileSizeInMB)) {
        setSelectedSupportingDocumentError('Upload a supporting document that is 10 MB or smaller.');
        return;
      } else if (!isValidImage) {
        setSelectedSupportingDocumentError('Select a valid image file.');
        return;
      } else {
        setSelectedSupportingDocumentError('');
      }
    }

    handleAddDocumentToUpload(documentType, file);
  };

  const handleDeleteExecutiveSummary = (): void => {
    const executiveSummaryDocumentToUploadIndex = nominationForStaffPageUiStore.documentToUpload.findIndex((document) =>
      document.documentType === 'executive-summary');
    if (executiveSummaryDocumentToUploadIndex >= 0) {
      nominationForStaffPageUiStore.documentToUpload.splice(executiveSummaryDocumentToUploadIndex, 1);
    }
    if ((nominationForStaffPageUiStore.executiveSummaryDocument?.documentId ?? -1) >= 0) {
      nominationForStaffPageUiStore.setDeleteExecutiveSummaryFile(true);
    }
    nominationForStaffPageUiStore.setExecutiveSummaryDocument(undefined);
  };

  const handleDeleteSupportingDocument = (documentId: number): void => {
    const supportingDocumentToUploadIndex = nominationForStaffPageUiStore.documentToUpload.findIndex((document) =>
      document.documentId === documentId);
    if (supportingDocumentToUploadIndex >= 0) {
      nominationForStaffPageUiStore.documentToUpload.splice(supportingDocumentToUploadIndex, 1);
    }

    const supportingDocumentIndex = nominationForStaffPageUiStore.nominationForStaff.nomination.supportingDocuments
      .findIndex((document) => document.documentId === documentId);
    if (supportingDocumentIndex >= 0) {
      nominationForStaffPageUiStore.nominationForStaff.nomination.supportingDocuments.splice(supportingDocumentIndex, 1);
    }

    if (documentId >= 0) {
      nominationForStaffPageUiStore.supportingDocumentIdsToDelete.push(documentId);
    }
  };

  const handleDownload = async (request: ResourceTicketRequest): Promise<void> => {
    nominationForStaffPageUiStore.setIsLoading(true);
    try {
      const resourceTicket = await apiStore.postResourceTicket(instance, request);
      downloadFile(resourceTicket.ticketId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
    }
  };

  const handleDownloadExecutiveSummary = async (): Promise<void> => {
    if (!nominationForStaffPageUiStore.executiveSummaryDocument) return;

    await handleDownload({
      data: {
        routeData: {
          nominationId: nominationForStaffPageUiStore.executiveSummaryDocument.nominationId,
        },
        queryString: {
          isDisplayedAsInline: `${isPdfFileName(nominationForStaffPageUiStore.executiveSummaryDocument.fileName)}`,
        },
      },
      resourceType: ResourceTypes.ExecutiveSummary,
    });
  };

  const handleDownloadSupportingDocument = async (supportingDocumentId: number, isDisplayedAsInline: boolean): Promise<void> => {
    await handleDownload({
      data: {
        routeData: {
          supportingDocumentId,
        },
        queryString: {
          isDisplayedAsInline: `${isDisplayedAsInline}`,
        },
      },
      resourceType: ResourceTypes.SupportingDocument,
    });
  };

  const renderAwardSummary = () => {
    return (
      <>
        <SummaryLayout
          title={<Button
            appearance='transparent'
            onClick={() => setAwardRequirementExpanded(!isAwardRequirementExpanded)}
            aria-expanded={isAwardRequirementExpanded}
            icon={isAwardRequirementExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
            iconPosition='after'
            className={styles.expandableTriggerButton}
          >
            Award requirement
          </Button>}
        />
        {
          isAwardRequirementExpanded && (
            <SummaryLayout withTopBorder>
              <div className={commonStyles.richTextContent}>
                {parse(getCleanHtmlString((userStore.isJudge ? nominationForStaffPageUiStore.selectedAward?.summary : undefined) ?? ''))}
              </div>
            </SummaryLayout>
          )
        }
      </>
    );
  }

  const renderJudgementQuestions = () => {
    return (
      nominationForStaffPageUiStore.nominationJudgement?.questions.map((question, index) => {
        return (
          <JudgingQuestion
            key={index}
            question={question}
            index={index + 1}
            isQuestionValidated={isJudgingValidated}
          />
        );
      })
    );
  }

  const renderFeedback = (): JSX.Element | null => {
    if (!nominationForStaffPageUiStore.nominationJudgement) return null;

    return (
      <SummaryLayout title='Feedback:'>
        <FormInput
          onChange={(value) => nominationForStaffPageUiStore.setJudgementFeedback(value)}
          validationMessage={(isJudgingValidated && nominationForStaffPageUiStore.nominationJudgement.feedback?.length > 1000) ? 'Use 1000 characters or fewer for feedback.' : ''}
          textAreaProps={{
            value: nominationForStaffPageUiStore.nominationJudgement.feedback ?? '',
            'aria-label': 'Feedback',
          }}
        />
      </SummaryLayout>
    );
  }

  const renderPartnerContacts = (contact: PartnerContact) => {
    return (
      <li key={contact.contactId}>
        <Text>{`${contact.contactName} - ${contact.contactEmail}`}</Text>
      </li>
    );
  }

  const renderTemplateUploadField = (isEditing: boolean): JSX.Element => {
    if (!isEditing) {
      return (
        <>
          {
            nominationForStaffPageUiStore.executiveSummaryDocument ? (
              <Link
                as='button'
                type='button'
                appearance='default'
                className={commonStyles.buttonAsLink}
                onClick={handleDownloadExecutiveSummary}
                disabled={nominationForStaffPageUiStore.executiveSummaryDocument === undefined}
              >
                {nominationForStaffPageUiStore.executiveSummaryDocument.fileName}
              </Link>
            ) : <Text>No response template file uploaded</Text>
          }
        </>
      );
    }

    return (
      <div className='upload-with-restore-field'>
        {
          !nominationForStaffPageUiStore.executiveSummaryDocument
            ? <Text>No response template file uploaded</Text>
            : (
              <DocumentFile
                onDownloadFile={handleDownloadExecutiveSummary}
                onDeleteFile={handleDeleteExecutiveSummary}
                filename={nominationForStaffPageUiStore.executiveSummaryDocument.fileName}
              />
            )
        }
        <FlexBox
          aligns='start'
          className='upload-with-restore-field__uploader'
        >
          <UploadButton
            onUpload={(fileList) => handleUploadFile('executive-summary', fileList)}
            validationMessage={executiveSummaryDocumentError || (nominationForStaffPageUiStore.nominationSubmissionValidation.fieldErrors.hasExecutiveSummaryDocument?.length ? <ValidationMessageList messages={nominationForStaffPageUiStore.nominationSubmissionValidation.fieldErrors.hasExecutiveSummaryDocument} /> : undefined)}
            buttonProps={{
              appearance: 'primary',
              icon: <AddCircleRegular />,
              children: 'Upload template',
              disabled: nominationForStaffPageUiStore.executiveSummaryDocument !== undefined,
            }}
          />
          <Button
            appearance='secondary'
            onClick={() => onRestoreDocument('executive-summary')}
          >
            Restore
          </Button>
        </FlexBox>
      </div>
    )
  };

  const renderWrittenNominationField = (isEditing: boolean): JSX.Element => {
    if (!isEditing) {
      return (<div>
        <div className={commonStyles.richTextContent}>
          {parse(getCleanHtmlString(nominationForStaffPageUiStore.displayedWrittenExecutiveSummary))}
        </div>
        {
          (nominationForStaffPageUiStore.isWrittenExecutiveSummaryCollapsible) &&
          <button
            className='button-as-link'
            aria-expanded={nominationForStaffPageUiStore.isNominationSubmissionExpanded}
            onClick={() => nominationForStaffPageUiStore.setNominationSubmissionExpanded(!nominationForStaffPageUiStore.isNominationSubmissionExpanded)}
          >
            {nominationForStaffPageUiStore.isNominationSubmissionExpanded ? 'Read less' : 'Read more'}
          </button>
        }
      </div>)
    };

    return (
      <RichTextEditor
        value={nominationForStaffPageUiStore.writtenExecutiveSummaryContent}
        onChange={(content) => nominationForStaffPageUiStore.setWrittenExecutiveSummary(content)}
        errorMessage={nominationForStaffPageUiStore.nominationSubmissionValidation.fieldErrors.writtenNominationContent?.length ? <ValidationMessageList messages={nominationForStaffPageUiStore.nominationSubmissionValidation.fieldErrors.writtenNominationContent} /> : undefined}
        showCharacterCounterWithMaxlength={8000}
      />
    )
  };

  const renderSupportingDocumentField = (isEditing: boolean) => (
    <div className='upload-with-restore-field'>
      {nominationForStaffPageUiStore.nominationForStaff.nomination.supportingDocuments.map((supportingDocument) => (
        <React.Fragment key={`${supportingDocument.documentId}${isEditing ? 'editing' : ''}`}>
          {
            isEditing ? (
              <DocumentFile
                onDownloadFile={() => handleDownloadSupportingDocument(supportingDocument.documentId, isPdfFileName(supportingDocument.fileName))}
                onDeleteFile={() => handleDeleteSupportingDocument(supportingDocument.documentId)}
                filename={supportingDocument.fileName}
              />
            ) : (
              <button
                className='button-as-link'
                onClick={() => handleDownloadSupportingDocument(supportingDocument.documentId, isPdfFileName(supportingDocument.fileName))}
              >
                {supportingDocument.fileName}
              </button>
            )
          }
        </React.Fragment>
      ))}
      {
        isEditing && (
          <FlexBox
            aligns='start'
            className='upload-with-restore-field__uploader'
          >
            <UploadButton
              onUpload={(fileList) => handleUploadFile('supporting-document', fileList)}
              validationMessage={selectedSupportingDocumentError}
              buttonProps={{
                appearance: 'primary',
                icon: <AddCircleRegular />,
                children: 'Upload supporting document',
              }}
            />
            <Button
              appearance='secondary'
              onClick={() => onRestoreDocument('supporting-document')}
            >
              Restore
            </Button>
          </FlexBox>
        )
      }
    </div>
  );

  return (
    <div className='nomination-for-staff-summary'>
      <FlexBox
        isFullWidth
        aligns='center'
        content='space-between'
        className='nomination-for-staff-summary__header'
      >
        <Text
          size={600}
          weight='semibold'
        >
          {`${nominationForStaffPageUiStore.nominationForStaff.nomination.nominationNumber} - ${nominationForStaffPageUiStore.nominationForStaff.nomination.name}`}
        </Text>
        <FlexBox className='header__buttons'>
          {
            (nominationForStaffPageUiStore.uploadedMedia.length > 0) &&
            <FlexBox
              as='button'
              aligns='center'
              onClick={() => downloadUploadedMedia()}
              className='button-as-link'
            >
              <ArrowDownload16Regular />
              Download media
            </FlexBox>
          }
          <FlexBox
            as='button'
            aligns='center'
            onClick={() => downloadExportedNomination(nominationForStaffPageUiStore.nominationForStaff.nominationId)}
            className='button-as-link'
          >
            <ArrowDownload16Regular />
            Download nomination
          </FlexBox>

          {
            userStore.isAdmin &&
            <>
              {
                nominationForStaffPageUiStore.isEditingMode ?
                  <FlexBox>
                    <Button
                      appearance='secondary'
                      onClick={() => onCancelEditNomination()}
                    >
                      Cancel
                    </Button>
                    <Button
                      appearance='primary'
                      onClick={() => onSaveNomination()}
                    >
                      Save
                    </Button>
                  </FlexBox> :
                  <Button
                    appearance='secondary'
                    className='edit-all-btn'
                    onClick={handleEditNomination}
                  >
                    Edit All
                  </Button>
              }
            </>
          }
        </FlexBox>
      </FlexBox>
      {
        nominationForStaffPageUiStore.isNominationValidationMessageVisible && (
          <MessageBar
            autoFocus
            className='nomination-for-staff-summary__validation-message'
            intent='error'
            role='alert'
            tabIndex={-1}
          >
            <MessageBarBody>
              The nomination was not saved. Correct the errors and try again.
            </MessageBarBody>
          </MessageBar>
        )
      }
      <div className='nomination-for-staff-summary__main'>
        {
          <SummaryLayout title='Abstract:'>
            {nominationForStaffPageUiStore.isEditingMode ? (
              <FormInput
                validationMessage={nominationForStaffPageUiStore.nominationSubmissionValidation.fieldErrors.abstract?.length ? <ValidationMessageList messages={nominationForStaffPageUiStore.nominationSubmissionValidation.fieldErrors.abstract} /> : undefined}
                onChange={(value) => nominationForStaffPageUiStore.nominationForStaff.nomination.abstract = value}
                textAreaProps={{
                  value: nominationForStaffPageUiStore.nominationForStaff.nomination.abstract,
                  spellCheck: true,
                }}
              />
            ) : <Text>{nominationForStaffPageUiStore.nominationForStaff.nomination.abstract}</Text>}
          </SummaryLayout>
        }
        {
          <SummaryLayout title='Nomination submission:'>
            {
              nominationForStaffPageUiStore.nominationForStaff.nomination.award.requireWrittenNomination
                ? renderWrittenNominationField(nominationForStaffPageUiStore.isEditingMode)
                : renderTemplateUploadField(nominationForStaffPageUiStore.isEditingMode)
            }
          </SummaryLayout>
        }
        {
          <SummaryLayout title='Supporting documents:'>{renderSupportingDocumentField(nominationForStaffPageUiStore.isEditingMode)}</SummaryLayout>
        }
        {
          <SummaryLayout title='Websites:'>
            <ul>
              {nominationForStaffPageUiStore.nominationForStaff.nomination.websites.map((website) => (
                <li key={website.websiteId}>
                  <Link
                    href={website.websiteURL}
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    {website.websiteURL}
                  </Link>
                </li>
              ))}
            </ul>
          </SummaryLayout>
        }
        {
          (userStore.isJudge && !userStore.isReviewer && !userStore.isLead && !userStore.isAdmin) &&
          <CustomerReferences />
        }
        {
          <SummaryLayout title='Qualifications:'>
            <ul>
              {nominationForStaffPageUiStore.nominationForStaff.qualifications.map((qualification, index) => {
                return (
                  <li key={index}>{qualification}</li>
                );
              })}
            </ul>
          </SummaryLayout>
        }
        {
          <SummaryLayout title='Partner Type:'>
            <div>
              {
                nominationForStaffPageUiStore.isEditingMode ?
                  <PartnerTypeSelectionDropdown
                    required
                    partnerTypeOptions={partnerTypeStore.partnerTypeOptions}
                    nominationPartnerTypes={nominationForStaffPageUiStore.nominationForStaff.nomination.partnerTypes}
                    validationMessage={
                      nominationForStaffPageUiStore.partnerTypeRequiredValidationMessages.length
                        ? <ValidationMessageList messages={nominationForStaffPageUiStore.partnerTypeRequiredValidationMessages} />
                        : undefined
                    }
                  /> :
                  <Text>{partnerTypeDisplayNames}</Text>
              }
            </div>
          </SummaryLayout>
        }
        {
          nominationForStaffPageUiStore.isEditingMode && nominationForStaffPageUiStore.nominationForStaff.nomination.partnerTypes.some((el) => partnerTypeStore.isOtherPartnerType(el.partnerTypeId)) &&
          <SummaryLayout title='Other Partner Type:'>
            <FormInput
              onChange={(value) => nominationForStaffPageUiStore.setOtherPartnerTypeDescription(value, partnerTypeStore.otherPartnerTypeId)}
              validationMessage={
                companyInformationValidation.fieldErrors.partnerTypes?.length
                  ? <ValidationMessageList messages={companyInformationValidation.fieldErrors.partnerTypes} />
                  : undefined
              }
              inputProps={{
                placeholder: 'Describe your partner type here',
                value: nominationForStaffPageUiStore.nominationForStaff.nomination.partnerTypes.find((el) => partnerTypeStore.isOtherPartnerType(el.partnerTypeId))?.description || '',
              }}
            />
          </SummaryLayout>
        }
        {
          <SummaryLayout title='Marketplace Offer:'>
            {
              nominationForStaffPageUiStore.isEditingMode ?
                <FormattedInput
                  prefix={'https://'}
                  value={nominationForStaffPageUiStore.nominationForStaff.nomination.marketplaceOfferUrl ?? ''}
                  onChange={marketplaceOfferUrl => nominationForStaffPageUiStore.setMarketplaceOfferUrl(marketplaceOfferUrl)}
                  errorText={
                    companyInformationValidation.fieldErrors.marketplaceOfferUrl?.length
                      ? <ValidationMessageList messages={companyInformationValidation.fieldErrors.marketplaceOfferUrl} />
                      : undefined
                  }
                  maxLength={512}
                  isValidated
                /> : <Text>{nominationForStaffPageUiStore.nominationForStaff.nomination.marketplaceOfferUrl}</Text>
            }
          </SummaryLayout>
        }
        {
          <SummaryLayout title='PDM Contact - Partner:'>
            <ul>
              {nominationForStaffPageUiStore.nominationForStaff.nomination.partnerContacts.filter(contact => contact.organizationTypeId === OrganizationType.Main).map(contact => renderPartnerContacts(contact))}
            </ul>
          </SummaryLayout>
        }
        {
          <SummaryLayout title='PDM Contact - Joint Nominee:'>
            <ul>
              {nominationForStaffPageUiStore.nominationForStaff.nomination.partnerContacts.filter(contact => contact.organizationTypeId === OrganizationType.JointNominee).map(contact => renderPartnerContacts(contact))}
            </ul>
          </SummaryLayout>
        }
        {
          nominationForStaffPageUiStore.isCountryAward && <SummaryLayout title='Category award:'>
            <Text>{nominationForStaffPageUiStore.nominationForStaff.nomination.award.title}</Text>
          </SummaryLayout>
        }
        {
          <SummaryLayout title='Case study candidate:'>
            <Checkbox
              label='This nomination is a case study candidate.'
              checked={nominationForStaffPageUiStore.nominationForStaff.isCaseStudy}
              onChange={(_, data) => onCaseStudyCheckedChange(data.checked as boolean)}
            />
          </SummaryLayout>
        }
        {
          awardStore.isJudgingWindowOpen && nominationForStaffPageUiStore.nominationJudgement !== undefined && nominationForStaffPageUiStore.assignedJudge !== undefined &&
          <div className='additional-nomination'>
            {renderJudgementQuestions()}
            {renderFeedback()}
          </div>
        }
        {(userStore.isJudge ? nominationForStaffPageUiStore.selectedAward?.summary : undefined) && renderAwardSummary()}
      </div >
    </div>
  );
}

export const NominationForStaffSummary = observer(NominationForStaffSummaryImpl);
